import React, { useEffect, useState, useRef } from 'react';
import './App.css';
import Admin from './pages/Admin';
import ApplicationForm from './pages/ApplicationForm';
import UserDashboardPage from './pages/UserDashboardPage';
import Welcome from './pages/Welcome';

import { ThemeProvider } from '@mui/material/styles';
import { Route, Routes } from "react-router-dom";
import SignUp from './components/registration/SignUp';
import CAppLoader from './components/utils/CAppLoader';
import useMultiTheme from "./custom-hooks/useMultiTheme";
import AadharDetails from './pages/AadharDetails';
import AddNewAddress from './pages/AddNewAddress';
import Analytics from './pages/Analytics';
import BankDetails from './pages/BankDetails';
import FaceMatchDetails from './pages/FaceMatchDetails';
import InstituteDetails from './pages/InstituteDetails';
import LoanAggrement from './pages/LoanAggrement';
import LoanApproved from './pages/LoanApproved';
import LoanDeclined from './pages/LoanDeclined';
import LoanRepayment from './pages/LoanRepayment';
import LoanStatus from './pages/LoanStatus';
import OtpLogin from './pages/OtpLogin';
import PanAadhaarDetails from './pages/PanAadhaarDetails';
import PaymentPage from './pages/PaymentPage';
import PersonalDetail from './pages/PersonalDetails';
import StudentDetail from './pages/StudentDetails';
import { useSelector } from 'react-redux';
import LoanCompleted from './pages/LoanCompleted';
import SelfEvaluation from './pages/SelfEvaluation';
import PANValidationAndAdditionalAddressPage from './components/forms/PANAndAdditionalAddressPage';
import PANValidationDetailsWrapper from './pages/PANValidationDetailsWrapper';
import AvanseNACHFlow from './pages/AvanseNACHFlow';
import AvansePaymentFlow from './pages/AvansePaymentFlow';
import axios from 'axios';
import { AuthProvider, useAuth } from './context/AuthContext';
import { baseURL } from './services/httpService';
import { userLogout } from './services/authenticationService';
import { CircularProgress } from '@mui/material';
import AadhaarAndUserSelfieMismatch from './pages/AdharAndUserSelfieMismatch';
import EmailVerificationPage from './pages/EmailVerificationPage';

const IDLE_TIMEOUT_MS = 5 * 60 * 1000;   // 5 minutes
const IDLE_EVENTS = ['mousemove', 'keydown', 'click', 'scroll'];
const PUBLIC_PATHS = ['/welcome', '/adminLogin', '/', '/evaluate', '/signup', '/signUp', '/otpLogin', '/verify-email'];

function AppRoutes() {
  const { muiTheme } = useMultiTheme();
  const { token, setAuth, clearAuth } = useAuth();
  const [authReady, setAuthReady] = useState(false);
  const [isOtherSelected, setIsOtherSelected] = useState(true);
  const idleTimer = useRef(null);
  const refreshCalled = useRef(false);
  const urlParams = window.location.pathname;

  // Boot-time silent refresh — restore session if refresh cookie exists
  // useRef guard prevents React StrictMode double-invocation from firing two concurrent requests
  useEffect(() => {
    if (refreshCalled.current) return;
    refreshCalled.current = true;
    axios.post(baseURL + '/api/client/auth/refresh', null, { withCredentials: true })
      .then(res => {
        if (res.data?.jwt) setAuth(res.data.jwt);
      })
      .catch(() => {
        // No valid refresh token — user will be redirected to welcome by guard below
      })
      .finally(() => setAuthReady(true));
  }, []);

  // 5-minute idle timeout — only active when user is logged in
  useEffect(() => {
    if (!token) return;

    const resetTimer = () => {
      clearTimeout(idleTimer.current);
      idleTimer.current = setTimeout(() => {
        clearAuth();
        userLogout().finally(() => { window.location.href = '/welcome'; });
      }, IDLE_TIMEOUT_MS);
    };

    IDLE_EVENTS.forEach(e => document.addEventListener(e, resetTimer, { passive: true }));
    resetTimer();

    return () => {
      clearTimeout(idleTimer.current);
      IDLE_EVENTS.forEach(e => document.removeEventListener(e, resetTimer));
    };
  }, [token]);

  // Show spinner while checking for existing session on boot
  if (!authReady) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </div>
    );
  }

  // Global auth guard — redirect to welcome if no token and not on a public path
  if (!token && !PUBLIC_PATHS.includes(urlParams)) {
    try {
      const decoded = atob(urlParams.substring(1));
      if (decoded.includes('byepass=true')) {
        localStorage.setItem('byepass', true);
        window.location.href = '/signup';
        return null;
      }
    } catch (_) {
      // not a base64 path, normal redirect
    }
    window.location.href = '/welcome';
    return null;
  }

  return (
    <ThemeProvider theme={muiTheme}>
      <div className="App">
        <Routes>
          <Route path='/' element={<Welcome adminLogin={false} />} />
          <Route path="/adminLogin" element={<Welcome adminLogin={true} />} />
          <Route path="/welcome" element={<Welcome adminLogin={false} />} />
          <Route path="/otpLogin" element={<OtpLogin />} />
          <Route path="/evaluate" element={<SelfEvaluation />} />
          <Route path="/signUp" element={<SignUp />} />
          <Route path="/instituteDetails" element={<InstituteDetails setIsOtherSelected={setIsOtherSelected} />} />
          <Route path="/studentDetails" element={<StudentDetail isOtherSelected={isOtherSelected} />} />
          <Route path="/personalDetails" element={<PersonalDetail />} />
          <Route path="/panAadharDetails" element={<PanAadhaarDetails />} />
          <Route path="/panVerificationWithAddress" element={<PANValidationDetailsWrapper />} />
          <Route path="/panAadharDetails/?requestId=:requestId&status=:status" element={<PanAadhaarDetails />} />
          <Route path="/aadhaarDetails" element={<AadharDetails />} />
          <Route path="/addNewAddress" element={<AddNewAddress />} />
          <Route path="/bankDetails" element={<BankDetails />} />
          <Route path="/bankDetails?ecres=:ecres&resdate=:resdate&fi=:fi" element={<BankDetails />} />
          <Route path="/faceMatch" element={<FaceMatchDetails />} />
          <Route path="/loanStatus" element={<LoanStatus />} />
          <Route path="/adharAndUserSelfieMismatch" element={<AadhaarAndUserSelfieMismatch />}></Route>
          <Route path="/verify-email" element={<EmailVerificationPage />} />
          <Route path="/loanDeclined" element={<LoanDeclined />} />
          <Route path="/loanApproved" element={<LoanApproved />} />
          <Route path="/loanAgreement" element={<LoanAggrement />} />
          <Route path="/loanRepayment" element={<LoanRepayment />} />
          <Route path="/avanse/nach" element={<AvanseNACHFlow />} />
          <Route path="/payment" element={<PaymentPage />} />
          <Route path="/avanse/payment" element={<AvansePaymentFlow />} />
          <Route path="/loanCompleted" element={<LoanCompleted />} />
          <Route path="/applicationForm" element={<ApplicationForm />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/analytics" element={<Analytics />} />
          <Route path="/userDashboard" element={<UserDashboardPage />} />
          <Route path="/panValidation" element={<PANValidationAndAdditionalAddressPage />} />
        </Routes>
        <CAppLoader />
      </div>
    </ThemeProvider>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  );
}

export default App;
